import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  GetUserDetailsResponse,
  UpdateUserDetailsResponse,
  UserDetails,
} from '@dreamai/types';
import { AddressFinder } from '@ideal-postcodes/address-finder';
import api from '../../api';
import {
  formatUkDateInput,
  isoDateToUk,
  parseUkDateToIso,
  validateUkDateOfBirth,
} from '../../utils/ukDate';
import './your-details-section.scss';

const YourDetailsSection: FC = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [details, setDetails] = useState<UserDetails | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [location, setLocation] = useState('');
  // DOB is displayed/edited in UK format (DD/MM/YYYY) and converted to ISO on submit.
  const [dateOfBirth, setDateOfBirth] = useState('');

  // Address finder
  const addressFinderInit = useRef(false);
  const addressInputRef = useRef<HTMLInputElement>(null);

  // Feedback
  const [feedback, setFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Track whether user has made changes
  const hasChanges = useCallback(() => {
    if (!details) return false;
    const existingDobUk = isoDateToUk(details.dateOfBirth);
    return (
      name.trim() !== (details.name || '') ||
      phoneNumber.trim() !== (details.phoneNumber || '') ||
      location.trim() !== (details.location || '') ||
      dateOfBirth.trim() !== existingDobUk
    );
  }, [details, name, phoneNumber, location, dateOfBirth]);

  // Fetch details
  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const token = await getAccessTokenSilently();
        const { data } = await api.get<GetUserDetailsResponse>(
          '/accounts/details',
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setDetails(data.details);
        setName(data.details.name || '');
        setPhoneNumber(data.details.phoneNumber || '');
        setLocation(data.details.location || '');
        setDateOfBirth(isoDateToUk(data.details.dateOfBirth));

        // Set uncontrolled address input value
        if (addressInputRef.current && data.details.location) {
          addressInputRef.current.value = data.details.location;
        }
      } catch (err) {
        console.error('Error fetching user details:', err);
        setFeedback({ type: 'error', message: 'Failed to load your details' });
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [getAccessTokenSilently]);

  // Ideal Postcodes Address Finder — only init once the form is rendered
  useEffect(() => {
    if (loading) return;
    if (addressFinderInit.current) return;
    addressFinderInit.current = true;

    const apiKey = process.env.REACT_APP_IDEAL_POSTCODES_API_KEY;
    if (!apiKey) return;

    AddressFinder.setup({
      apiKey,
      inputField: '#settings-address-input',
      outputFields: {},
      detectCountry: false,
      defaultCountry: 'GBR',
      restrictCountries: ['GBR'],
      onAddressRetrieved: (address) => {
        const a = address as Record<string, string>;
        const formatted = [
          a.line_1,
          a.line_2,
          a.line_3,
          a.post_town,
          a.postcode,
        ]
          .filter((part) => part && part !== '')
          .join(', ');
        if (addressInputRef.current) {
          addressInputRef.current.value = formatted;
        }
        setLocation(formatted);
      },
    });
  }, [loading]);

  const validate = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    if (!name.trim()) {
      errors.name = 'Name is required';
    }
    const trimmedPhone = phoneNumber.trim();
    if (!trimmedPhone) {
      errors.phoneNumber = 'Phone number is required';
    } else if (trimmedPhone.replace(/\D/g, '').length < 7) {
      errors.phoneNumber = 'Please enter a valid phone number';
    }
    // Address is collected via an uncontrolled input; fall back to the DOM value
    // if the typed location state is empty (e.g. user never picked a suggestion).
    const addr = location || addressInputRef.current?.value?.trim() || '';
    if (!addr) {
      errors.location = 'Address is required';
    }
    const dobErr = validateUkDateOfBirth(dateOfBirth);
    if (dobErr) {
      errors.dateOfBirth = dobErr;
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [name, phoneNumber, location, dateOfBirth]);

  const handleSave = useCallback(async () => {
    if (!validate()) return;

    setSaving(true);
    setFeedback(null);

    try {
      const token = await getAccessTokenSilently();
      const addr = location || addressInputRef.current?.value?.trim() || '';
      const isoDob = parseUkDateToIso(dateOfBirth.trim());
      if (!isoDob) {
        // validate() already guards this, but TypeScript needs the narrowing.
        setFieldErrors((prev) => ({
          ...prev,
          dateOfBirth: 'Please enter a valid date as DD/MM/YYYY',
        }));
        setSaving(false);
        return;
      }

      const { data } = await api.put<UpdateUserDetailsResponse>(
        '/accounts/details',
        {
          name: name.trim(),
          phoneNumber: phoneNumber.trim(),
          location: addr.trim(),
          dateOfBirth: isoDob,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setDetails(data.details);
      setName(data.details.name || '');
      setPhoneNumber(data.details.phoneNumber || '');
      setLocation(data.details.location || '');
      setDateOfBirth(isoDateToUk(data.details.dateOfBirth));
      setFeedback({ type: 'success', message: 'Details saved' });

      // Clear success message after 3s
      setTimeout(() => setFeedback(null), 3000);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      const msg =
        axiosErr?.response?.data?.error || 'Failed to save. Please try again.';
      setFeedback({ type: 'error', message: msg });
    } finally {
      setSaving(false);
    }
  }, [
    name,
    phoneNumber,
    location,
    dateOfBirth,
    validate,
    getAccessTokenSilently,
  ]);

  if (loading) {
    return (
      <div className='your-details-section'>
        <div className='settings-skeleton' />
      </div>
    );
  }

  return (
    <div className='your-details-section'>
      <div className='your-details-header'>
        <h2 className='section-title'>Your details</h2>
        <p className='your-details-subtitle'>
          Update your personal information.
        </p>
      </div>

      <div className='your-details-card'>
        <div className='your-details-field'>
          <label className='field-label' htmlFor='yd-name'>
            Your name
          </label>
          <input
            id='yd-name'
            type='text'
            className={`field-input${fieldErrors.name ? ' has-error' : ''}`}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (fieldErrors.name)
                setFieldErrors((prev) => ({ ...prev, name: '' }));
            }}
          />
          {fieldErrors.name && (
            <p className='field-error'>{fieldErrors.name}</p>
          )}
        </div>

        <div className='your-details-field'>
          <label className='field-label' htmlFor='yd-email'>
            Email
          </label>
          <input
            id='yd-email'
            type='email'
            className='field-input disabled'
            value={details?.email || ''}
            disabled
          />
          <p className='field-hint'>
            This is your login email and cannot be changed here.
          </p>
        </div>

        <div className='your-details-field address-field'>
          <label className='field-label' htmlFor='settings-address-input'>
            Address
          </label>
          <p className='field-hint'>
            Type the first line of your address to search
          </p>
          <input
            ref={addressInputRef}
            id='settings-address-input'
            type='text'
            className='field-input'
            autoComplete='off'
            defaultValue={details?.location || ''}
            onChange={(e) => setLocation(e.target.value)}
          />
        </div>

        <div className='your-details-field'>
          <label className='field-label' htmlFor='yd-phone'>
            Phone number
          </label>
          <input
            id='yd-phone'
            type='tel'
            className={`field-input${fieldErrors.phoneNumber ? ' has-error' : ''}`}
            value={phoneNumber}
            onChange={(e) => {
              setPhoneNumber(e.target.value);
              if (fieldErrors.phoneNumber)
                setFieldErrors((prev) => ({ ...prev, phoneNumber: '' }));
            }}
          />
          {fieldErrors.phoneNumber && (
            <p className='field-error'>{fieldErrors.phoneNumber}</p>
          )}
        </div>

        <div className='your-details-field'>
          <label className='field-label' htmlFor='yd-dob'>
            Date of birth
          </label>
          <p className='field-hint'>Format: DD/MM/YYYY</p>
          <input
            id='yd-dob'
            type='text'
            inputMode='numeric'
            autoComplete='bday'
            className={`field-input${fieldErrors.dateOfBirth ? ' has-error' : ''}`}
            value={dateOfBirth}
            onChange={(e) => {
              setDateOfBirth(formatUkDateInput(e.target.value));
              if (fieldErrors.dateOfBirth)
                setFieldErrors((prev) => ({ ...prev, dateOfBirth: '' }));
            }}
            placeholder='DD/MM/YYYY'
            maxLength={10}
          />
          {fieldErrors.dateOfBirth && (
            <p className='field-error'>{fieldErrors.dateOfBirth}</p>
          )}
        </div>
      </div>

      {feedback && (
        <p className={`your-details-feedback ${feedback.type}`}>
          {feedback.message}
        </p>
      )}

      <div className='your-details-actions'>
        <button
          className='btn-save'
          onClick={handleSave}
          disabled={saving || !hasChanges()}
        >
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </div>
  );
};

export default YourDetailsSection;
